import { pxToEm } from 'util/pxToEm';
import { Box, Typography } from '@mui/material';
import { AddIcon } from 'components/icons';
import React, { ReactElement, useCallback, useEffect, useState } from 'react';
import { useSchoolsApi } from 'services/api';
import useSWR from 'swr';
import {
  Facility,
  FacilityApprover,
} from 'services/api/apis/organizations/dto/list-facilities.dto';
import { ManageFacilitiesModal } from 'components/modals/ManageFacilitiesModal';
import { colorLightGrey, colorWhite, colorLightBlueWhite } from 'customColors';
import EditIconSquare from '../../../../assets/icons/edit.svg';

export interface FacilitiesTabProps {
  schoolId: string;
}

export const FacilitiesTab = ({
  schoolId,
}: FacilitiesTabProps): ReactElement => {
  const schoolsApi = useSchoolsApi();

  const [facilities, setFacilities] = useState<Facility[]>([]);
  const [
    isManageFacilitiesModalOpen,
    setIsManageFacilitiesModalOpen,
  ] = useState(false);
  const [selectedFacility, setSelectedFacility] = useState<Facility | null>(
    null,
  );
  const [modalMode, setModalMode] = useState<'create' | 'edit'>('create');

  const rSchool = useSWR(
    schoolId ? `/schools/${schoolId}` : null,
    async () => {
      if (!schoolId) return;

      const result = await schoolsApi.getSchool(schoolId);

      if (result.success) return result.value;

      if (result.error.response?.status === 401) return;

      throw new Error();
    },
    {
      revalidateOnFocus: false,
      refreshInterval: 0,
    },
  );

  const getFacilitiesList = useCallback(async () => {
    const result = await schoolsApi.getSchoolFacilities(schoolId);
    if (result.success) setFacilities(result.value.facilities);
    else throw new Error(result.error.message);
  }, [schoolId]);

  useEffect(() => {
    getFacilitiesList();
  }, [getFacilitiesList]);

  const sortedFacilities = [...facilities].sort((a, b) =>
    a.name.localeCompare(b.name),
  );

  return (
    <Box>
      <Box
        mt={30}
        display="flex"
        justifyContent={{ xs: 'space-between', md: 'flex-start' }}
        alignItems="center"
        gap={32}
      >
        <Typography sx={{ fontWeight: 600, fontSize: '1.25em' }}>
          Facilities
        </Typography>
        <Box
          display="flex"
          alignItems="center"
          gap={8}
          style={{ cursor: 'pointer' }}
          onClick={() => {
            setSelectedFacility(null);
            setModalMode('create');
            setIsManageFacilitiesModalOpen(true);
          }}
        >
          <AddIcon />
          Add Facility
        </Box>
      </Box>

      {sortedFacilities.length === 0 ? (
        <Box mt={30} display="flex" justifyContent="center" alignItems="center">
          <Typography sx={{ fontWeight: 400, fontSize: '1rem' }}>
            No facilities found
          </Typography>
        </Box>
      ) : (
        <Box
          mt={24}
          display="grid"
          gridTemplateColumns={{
            xs: '1fr',
            md: 'repeat(auto-fill, 352px)',
          }}
          gap={{ xs: 16, md: 32 }}
        >
          {sortedFacilities.map(facility => {
            const approverNames = facility.approvers
              .sort((a, b) => a.approverName.localeCompare(b.approverName))
              .map((approver: FacilityApprover) => approver.approverName)
              .join(', ');
            return (
              <Box
                key={facility.id}
                sx={{
                  width: { xs: 320, md: 352 },
                  height: 148,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  padding: 16,
                  gap: 0,
                  borderRadius: '8px',
                  border: `1px solid ${colorLightGrey}`,
                  backgroundColor: colorLightBlueWhite,
                  boxShadow: 'none',
                  overflow: 'hidden',
                  '&:hover': {
                    boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.08)',
                  },
                }}
              >
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="flex-start"
                  gap={16}
                  width="100%"
                  mb={16}
                >
                  <Typography
                    fontSize={14}
                    fontWeight={500}
                    sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      wordBreak: 'break-word',
                      flex: 1,
                      lineHeight: 1.5,
                    }}
                  >
                    {facility.name}
                  </Typography>
                  <Box
                    display="flex"
                    alignItems="center"
                    gap={4}
                    style={{ cursor: 'pointer' }}
                    sx={{
                      flexShrink: 0,
                      borderRadius: '6px',
                      border: `1px solid ${colorLightGrey}`,
                      backgroundColor: `${colorWhite}`,
                      padding: '8px 16px',
                    }}
                    onClick={() => {
                      setSelectedFacility(facility);
                      setModalMode('edit');
                      setIsManageFacilitiesModalOpen(true);
                    }}
                  >
                    <EditIconSquare />
                    <Typography fontWeight={400} fontSize={pxToEm(14)}>
                      Edit
                    </Typography>
                  </Box>
                </Box>
                {rSchool.data?.isMultiStepApprovalEnabled && approverNames && (
                  <Typography
                    fontSize={pxToEm(14)}
                    fontWeight={400}
                    sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      wordBreak: 'break-word',
                      lineHeight: 1.5,
                      mt: 'auto',
                      mb: 0,
                    }}
                  >
                    {approverNames}
                  </Typography>
                )}
              </Box>
            );
          })}
        </Box>
      )}

      {isManageFacilitiesModalOpen && (
        <ManageFacilitiesModal
          schoolId={schoolId}
          isMultiStepApprovalEnabled={
            rSchool.data?.isMultiStepApprovalEnabled ?? false
          }
          facility={selectedFacility}
          mode={modalMode}
          open={isManageFacilitiesModalOpen}
          onClose={() => {
            setIsManageFacilitiesModalOpen(false);
            setSelectedFacility(null);
            void getFacilitiesList();
          }}
        />
      )}
    </Box>
  );
};
